// Patched version of react-overlays/lib/RootCloseWrapper for React 18 compatibility.
//
// In React 17+, event delegation moved from document to the root container.
// When the user clicks to open a dropdown, React processes the click at the root
// container, opens the menu, and RootCloseWrapper adds document listeners in
// componentDidUpdate. The original click is still bubbling toward document at
// that point, so the new bubble listener fires immediately and closes the menu.
//
// Fix: wrap addEventListeners in setTimeout(0) so the listeners are registered
// in the next tick, after the current click event has fully propagated.

import contains from 'dom-helpers/query/contains';
import React from 'react';
import ReactDOM from 'react-dom';
import addEventListener from 'react-overlays/lib/utils/addEventListener';
import ownerDocument from 'react-overlays/lib/utils/ownerDocument';

const escapeKeyCode = 27;
const isLeftClickEvent = (e) => e.button === 0;
const isModifiedEvent = (e) => !!(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey);

class RootCloseWrapper extends React.Component {
  constructor(props, context) {
    super(props, context);
    this._unmounted = false;
    this.preventMouseRootClose = false;

    this.handleMouseCapture = (e) => {
      this.preventMouseRootClose =
        isModifiedEvent(e) || !isLeftClickEvent(e) ||
        contains(ReactDOM.findDOMNode(this), e.target);
    };

    this.handleMouse = (e) => {
      if (!this.preventMouseRootClose && this.props.onRootClose) {
        this.props.onRootClose(e);
      }
    };

    this.handleKeyUp = (e) => {
      if (e.keyCode === escapeKeyCode && this.props.onRootClose) {
        this.props.onRootClose(e);
      }
    };

    this.addEventListeners = () => {
      setTimeout(() => {
        if (this._unmounted) return;
        const event = this.props.event;
        const doc = ownerDocument(this);
        this.documentMouseCaptureListener = addEventListener(doc, event, this.handleMouseCapture, true);
        this.documentMouseListener = addEventListener(doc, event, this.handleMouse);
        this.documentKeyupListener = addEventListener(doc, 'keyup', this.handleKeyUp);
      }, 0);
    };

    this.removeEventListeners = () => {
      if (this.documentMouseCaptureListener) this.documentMouseCaptureListener.remove();
      if (this.documentMouseListener) this.documentMouseListener.remove();
      if (this.documentKeyupListener) this.documentKeyupListener.remove();
    };
  }

  componentDidMount() {
    if (!this.props.disabled) this.addEventListeners();
  }

  componentDidUpdate(prevProps) {
    if (!this.props.disabled && prevProps.disabled) {
      this.addEventListeners();
    } else if (this.props.disabled && !prevProps.disabled) {
      this.removeEventListeners();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (!this.props.disabled) this.removeEventListeners();
  }

  render() {
    return this.props.children;
  }
}

RootCloseWrapper.displayName = 'RootCloseWrapper';
RootCloseWrapper.defaultProps = { event: 'click' };

export default RootCloseWrapper;
